import { useState, useCallback } from 'react';
import {
  trackComparisonSelectionStarted,
  trackComparisonSelectionAborted,
  AbortComparisonSelectionSource,
  trackComparisonSelectionChanged,
  ChangeComparisonSelectionSource,
} from '../areas/product-compare/tracking';
import { Product } from '../types';

export type ProductSelectedToCompare = Pick<
  Product,
  'id' | 'name' | 'pathName'
> & {
  media: Pick<Product['media'], 'first'>;
  priceSummary: Pick<Product['priceSummary'], 'regular' | 'alternative'>;
};

export type SelectProductToCompare = (
  source: ChangeComparisonSelectionSource,
  product: ProductSelectedToCompare
) => void;
export type DeselectProductToCompare = SelectProductToCompare;
export type SelectProductToCompareById = (
  productId: number,
  source: ChangeComparisonSelectionSource
) => void;
export type DeselectProductToCompareById = SelectProductToCompareById;

const MAX_PRODUCTS_TO_COMPARE = 40;
/**
 * Does not store state in a context or anything like that, so you cannot use
 * it multiple times in the same context/page and expect state to be shared across usages
 */
export const useCompareProductsSelection = (): CompareProductsSelectionType => {
  const [isSelectingProducts, setIsSelectingProducts] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<
    ProductSelectedToCompare[]
  >([]);
  const startProductSelection = useCallback(() => {
    setIsSelectingProducts(true);
    trackComparisonSelectionStarted();
  }, []);
  const abortProductSelection = useCallback(
    (source: AbortComparisonSelectionSource) => {
      setIsSelectingProducts(false);
      setSelectedProducts([]);
      trackComparisonSelectionAborted(source);
    },
    []
  );

  const selectProduct = useCallback<SelectProductToCompare>(
    (source, product) => {
      if (
        selectedProducts.some(({ id }) => id === product.id) ||
        selectedProducts.length > MAX_PRODUCTS_TO_COMPARE
      ) {
        return;
      }

      const products = [...selectedProducts, product];
      setSelectedProducts(products);
      trackComparisonSelectionChanged(products.length, 'add', source);
    },
    [selectedProducts]
  );

  const deselectProduct = useCallback<DeselectProductToCompare>(
    (source, product) => {
      const products = selectedProducts.filter(({ id }) => id !== product.id);
      setSelectedProducts(products);
      trackComparisonSelectionChanged(products.length, 'remove', source);
    },
    [selectedProducts]
  );

  return {
    isSelectingProducts,
    selectedProducts,
    startProductSelection,
    abortProductSelection,
    selectProduct,
    deselectProduct,
  };
};

export interface CompareProductsSelectionType {
  isSelectingProducts: boolean;
  selectedProducts: ProductSelectedToCompare[];
  startProductSelection: () => void;
  abortProductSelection: (source: AbortComparisonSelectionSource) => void;
  selectProduct: SelectProductToCompare;
  deselectProduct: SelectProductToCompare;
}
